import React, {Component} from 'react';
import PropTypes from 'prop-types';
import reactDom from 'react-dom';
import classNames from 'classnames';
import {FormInput} from '@homeaway/react-form-components';
import determineIfMobile from '@homeaway/react-determine-if-mobile';
import propTypeDateString from '../../utils/propTypeDateString';
import {getMonthYear, getLocaleDateString, getDateToString, compareDateStrings} from '../../utils/dateUtils';
import Base from '../Base/Base';

class DatePicker extends Component {
    static propTypes = {
        /** Optional labels object to override default locale-driven labels */
        calendarLabels: PropTypes.shape({
            /** For locales that need a year label while displaying date e.g. ja-JP*/
            yearLabel: PropTypes.string,
            monthNames: PropTypes.arrayOf(PropTypes.string),
            shortMonthNames: PropTypes.arrayOf(PropTypes.string),
            dayNames: PropTypes.arrayOf(PropTypes.string),
            shortDayNames: PropTypes.arrayOf(PropTypes.string)
        }),
        /** The text label displayed on the clear button */
        clearButtonLabel: PropTypes.string,
        /** The aria label used for screen readers to describe how to use the date picker. */
        directionsLabel: PropTypes.string,
        /** Selected date */
        date: propTypeDateString,
        /** Options object, eg. {month: 'short', day: 'numeric'} (See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString) */
        dateFormatOptions: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** DayData that will power any passed in template */
        dayData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
        /** An optional passed in React component, used to render each day. **/
        dayTemplateComponent: PropTypes.func,
        /** Prevents keyboard input. Useful on iPad to prevent software keyboard from popping up. */
        disableTextInput: PropTypes.bool,
        /** Form input's error message */
        errorMsg: PropTypes.node,
        /** Determines whether the date picker is open or not */
        isOpen: PropTypes.bool,
        /** Form input's label */
        label: PropTypes.string.isRequired,
        /** A moniker or BCP-47 style language tag string. */
        locale: PropTypes.string,
        /** The title of the modal displayed on mobile */
        modalTitle: PropTypes.string,
        /** Maximum allowable date after which the next button will be disabled; scrollable mode stops loading more months. */
        maxDate: PropTypes.instanceOf(Date),
        /** Minimum allowable date before which the prev button will be disabled; scrollable mode uses month and year props. */
        minDate: PropTypes.instanceOf(Date),
        /** Form input's name property */
        name: PropTypes.string.isRequired,
        /** Function to call when a date is selected on the calendar */
        onDateChange: PropTypes.func,
        /** Function to call when the date is cleared */
        onDateClear: PropTypes.func,
        /** An optional passed in React component, used to trigger Next Available arrival and departure dates when clicked on disabled dates. **/
        onDisabledDayClick: PropTypes.func,
        /** Optional callback for when the date picker gains focus */
        onFocus: PropTypes.func,
        /** Function to call when the date picker opens */
        onOpen: PropTypes.func,
        /** Function to call when a date picker is closed */
        onClose: PropTypes.func,
        /** Optional callback when date picker is submitted (continue) */
        onSubmit: PropTypes.func,
        /** Options callback when date picker is closed (X, Esc) */
        onCancel: PropTypes.func,
        /** The DOM element to use as the parent for the date picker when visible. This is useful in scenarios where the date picker needs to break out of its current container due to overflow or z-index issues. */
        parentNode: Base.checkElementType,
        /** Display-only mode */
        readOnly: PropTypes.bool,
        /** The DOM element that the date picker needs to scroll with. Only specify this if parentNode was specified and the date picker is displayed in a container that is scrollable independent of the browser window. */
        scrollNode: Base.checkElementType,
        /** Show "continue" button in modal on mobile */
        showContinueButton: PropTypes.bool,
        /** By default, the input element is given a data-wdio attribute of "date-picker-input".
        If you have more than one on a page you can add a prefix by using this property. */
        wdioPrefix: PropTypes.bool,
        /** Flag that specifies whether year should be displayed first, useful in locales like ja-JP*/
        displayYearFirst: PropTypes.bool,
        /** Duration of the fade transition when the tooltip closes in milliseconds */
        fadeOutDuration: PropTypes.number,
        /** FormInput's id, required for a11y */
        id: PropTypes.string.isRequired
    }

    static defaultProps = {
        calendarLabels: {
            yearLabel: ''
        },
        directionsLabel: 'Check in date expanded. Choose a date from the table below. Tab into the table and use the arrow keys for navigation.',
        dateFormatOptions: {},
        disableTextInput: false,
        isOpen: false,
        modalTitle: 'Select a date',
        onClose: () => {},
        onDateChange: () => {},
        onDateClear: () => {},
        onFocus: () => {},
        onOpen: () => {},
        readOnly: false,
        displayYearFirst: false,
        showContinueButton: false,
        fadeOutDuration: 400
    }

    constructor(props) {
        super(props);

        const {isOpen, date} = props;
        const today = new Date();
        const todayString = getDateToString(today);
        let {month, year} = getMonthYear(date);

        if (date) {
            // If startDate is in the future, we need to reset the month & year to the current date.
            if (compareDateStrings(todayString, date) === 1) {
                month = today.getMonth();
                year = today.getFullYear();
            }
        }

        this.state = {
            month,
            year,
            isOpen
        };
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.isOpen !== nextProps.isOpen && this.state.isOpen !== nextProps.isOpen) {
            if (nextProps.isOpen) {
                this.handleFocus();
            } else {
                this.handleClose();
            }
        }
    }

    componentDidUpdate() {
        // When rendering the calendar in the modal, we display 24 months and allow scrolling.
        // Rendering the 24 months immediately screws up the CSS transitions and makes things
        // janky. So we display an empty modal first, then start rendering the calendar.
        if (this.props.isMobile && this.state.isDelayed) {
            window.setTimeout(() => {
                this.setState({isDelayed: false});
            }, 50);
        }
    }

    handleFocus = () => {
        const {isMobile, date, disableTextInput} = this.props;
        const {month, year} = getMonthYear(date);

        if (isMobile && !this.state.isClosing) {
            // Open, but set isDelayed to true so the calendar waits a moment to render.
            this.setState({
                isOpen: true,
                isDelayed: true,
            });

            if (date) {
                const today = new Date();
                const todayString = getDateToString(today);

                // If startDate is in the future, we need to reset the month & year to the current date.
                if (compareDateStrings(todayString, date) === 1) {
                    this.setState({
                        month: today.getMonth(),
                        year: today.getFullYear()
                    });
                }
            }
        } else if (document && !this.state.isClosing) {
            const dateInput = reactDom.findDOMNode(this).querySelector('input');

            // prevent double setState when dateInput receives focus;
            // as we may be clicking on the overlay, rather than tabbing to the input
            // force focus of the input for text entry (text entry is TODO)
            if (!disableTextInput && document.activeElement !== dateInput) {
                dateInput.focus();
            }

            this.setState({
                isOpen: true,
                month,
                year
            });
        }
    }

    handleClose = (callback) => {
        if (this.state.isOpen) {
            this.setState({
                isOpen: false,
                isClosing: true
            }, () => {
                this.props.onClose();

                if (callback) {
                    callback();
                }
            });
        }
    }

    handleFinishClose = () => {
        this.setState({
            isClosing: false
        });
    }

    handleOpen = () => {
        if (this.state.isOpen) {
            this.props.onOpen();
        }
    }

    contains(elem, target) {
        return elem && reactDom.findDOMNode(elem).contains(target);
    }

    handleDateChange = (dateString) => {
        this.props.onDateChange(dateString);

        // Delaying to allow selection visual to appear before closing
        window.setTimeout(() => {
            // Selection and close of the picker is a "submit" so pass the onSubmit
            // handler to handleClose so it will be invoked.
            this.handleClose(this.props.onSubmit);
        }, 20);
    }

    handleClearDates = (event) => {
        const {onDateChange, onDateClear} = this.props;
        event.preventDefault();
        onDateChange('');
        onDateClear();
    }

    // Due to a11y, onKeydown is handled differently than the onClick for the clear button.
    // The clear button disables itself after being pressed, which then moves focus to the document body by browser default.
    // To prevent us from losing focus to the body, we reset the focused element to the first
    // focusable element in DatePicker before performing the clear.
    handleKeydownClearDates = (e, ref) => {
        const {onDateChange, onDateClear} = this.props;
        const keycode = e.which;

        if (keycode === 13 || keycode === 32) { // Enter key or spacebar
            e.preventDefault();
            e.stopPropagation(); // Prevents the initial keydown on the clear button from progating to the new focused element

            ref.resetFocus();

            onDateChange('');
            onDateClear();
        }
    }

    handleChangeInput() {
        // TODO: allow keyboard entry
    }

    calendarRefFunc= (c) => {
        this.calendar = c;
    }

    getWdio(prefix, elementClass) {
        return prefix ? `${prefix}-${elementClass}` : elementClass;
    }

    renderInput() {
        const {date, label, name, errorMsg, className, readOnly, wdioPrefix, locale, dateFormatOptions, directionsLabel, id} = this.props;

        const inputClasses = classNames(className, {
            'date-picker__input': true,
            'not-empty': this.state.isOpen
        });

        const overlayClasses = classNames('picker__overlay', {
            'picker__overlay--selectable': !readOnly
        });

        const inputValue = getLocaleDateString(date, locale, dateFormatOptions);

        return (
            <div className="position-relative">
                <FormInput
                    className={inputClasses}
                    name={name}
                    label={label}
                    value={inputValue}
                    onChange={this.handleChangeInput}
                    onFocus={readOnly ? null : this.handleFocus}
                    errorMsg={errorMsg}
                    ref={(c) => (this.input = c)}
                    readOnly={'readOnly'}
                    data-wdio={this.getWdio(wdioPrefix, 'date-picker-input')}
                    aria-label={readOnly ? '' : directionsLabel}
                    id={`input-${id}`}
                />
                <div
                    className={overlayClasses}
                    ref={(c) => (this.overlay = c)}
                    onClick={readOnly ? null : this.handleFocus}
                />
            </div>
        );
    }

    render() {
        const classes = classNames(this.props.className, 'date-picker');

        return (
            <Base
                fadeOutDuration={this.props.fadeOutDuration}
                calendarRefFunc={this.calendarRefFunc}
                className={classes}
                clearButtonLabel={this.props.clearButtonLabel}
                date={this.props.date}
                dayData={this.props.dayData}
                dayTemplateComponent={this.props.dayTemplateComponent}
                inputs={this.renderInput()}
                isDelayed={this.state.isDelayed}
                isMobile={this.props.isMobile}
                isOpen={this.state.isOpen}
                calendarLabels={this.props.calendarLabels}
                locale={this.props.locale}
                modalTitle={this.props.modalTitle}
                month={this.state.month}
                maxDate={this.props.maxDate}
                minDate={this.props.minDate}
                numMonthsToDisplay={1}
                onDatesCleared={this.handleClearDates}
                onKeydownDatesCleared={this.handleKeydownClearDates}
                onDisabledDayClick={this.props.onDisabledDayClick}
                onFocus={this.props.onFocus}
                onOpen={this.handleOpen}
                onClose={this.handleClose}
                onFinishClose={this.handleFinishClose}
                onSubmit={this.props.onSubmit}
                onCancel={this.props.onCancel}
                onSelectionChanged={this.handleDateChange}
                parentNode={this.props.parentNode}
                scrollNode={this.props.scrollNode}
                selectionChangeMode="all"
                selectionType="single"
                showContinueButton={this.props.showContinueButton}
                displayYearFirst={this.props.displayYearFirst}
                year={this.state.year}
            />
        );
    }
}

export default determineIfMobile(DatePicker);
